// ==================== FORM INPUT COMPONENT ====================

import { forwardRef, useState, useId } from 'react';
import { Eye, EyeOff, AlertCircle, CheckCircle2 } from 'lucide-react';
import { cn } from '../../utils/helpers';

interface FormInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  success?: string;
  hint?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  showPasswordToggle?: boolean;
  fullWidth?: boolean;
}

const FormInput = forwardRef<HTMLInputElement, FormInputProps>(
  (
    {
      label,
      error,
      success,
      hint,
      leftIcon,
      rightIcon,
      showPasswordToggle = false,
      fullWidth = true,
      type = 'text',
      className,
      disabled,
      id,
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false);
    const generatedId = useId();
    const inputId = id || generatedId;
    
    const isPassword = type === 'password';
    const inputType = isPassword && showPassword ? 'text' : type;

    const baseInputStyles = `
      w-full px-4 py-3 rounded-md
      bg-(--color-charcoal-black) 
      border border-(--color-border-gray)
      text-white placeholder:text-(--color-muted-gray)
      focus:outline-none focus:ring-2 focus:ring-(--color-signal-orange)/50 focus:border-(--color-signal-orange)
      transition-all duration-200
      disabled:opacity-50 disabled:cursor-not-allowed
    `;

    const errorStyles = error ? 'border-(--color-error) focus:ring-(--color-error)/50 focus:border-(--color-error)' : '';
    const successStyles = success ? 'border-(--color-success) focus:ring-(--color-success)/50 focus:border-(--color-success)' : '';
    const iconPaddingLeft = leftIcon ? 'pl-11' : '';
    const iconPaddingRight = (rightIcon || showPasswordToggle || error || success) ? 'pr-11' : '';

    return (
      <div className={cn('relative', fullWidth && 'w-full')}>
        {/* Label */}
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-(--color-soft-gray) mb-2"
          >
            {label}
            {props.required && <span className="text-(--color-error) ml-1">*</span>}
          </label>
        )}

        {/* Input Wrapper */}
        <div className="relative">
          {/* Left Icon */}
          {leftIcon && (
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-(--color-muted-gray)">
              {leftIcon}
            </div>
          )}

          {/* Input */}
          <input
            ref={ref}
            id={inputId}
            type={inputType}
            disabled={disabled}
            className={cn(
              baseInputStyles,
              errorStyles,
              successStyles,
              iconPaddingLeft,
              iconPaddingRight,
              className
            )}
            {...props}
          />

          {/* Right Icons */}
          <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2">
            {/* Password Toggle */}
            {isPassword && showPasswordToggle && (
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="text-(--color-muted-gray) hover:text-white transition-colors"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            )}

            {/* Error Icon */}
            {error && !rightIcon && (
              <AlertCircle size={18} className="text-(--color-error)" />
            )}

            {/* Success Icon */}
            {success && !error && !rightIcon && (
              <CheckCircle2 size={18} className="text-(--color-success)" />
            )}

            {/* Custom Right Icon */}
            {rightIcon && !isPassword && (
              <span className="text-(--color-muted-gray)">{rightIcon}</span>
            )}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <p className="mt-2 text-sm text-(--color-error) flex items-center gap-1">
            <AlertCircle size={14} />
            {error}
          </p>
        )}

        {/* Success Message */}
        {success && !error && (
          <p className="mt-2 text-sm text-(--color-success) flex items-center gap-1">
            <CheckCircle2 size={14} />
            {success}
          </p>
        )}

        {/* Hint */}
        {hint && !error && !success && (
          <p className="mt-2 text-sm text-(--color-muted-gray)">{hint}</p>
        )}
      </div>
    );
  }
);

FormInput.displayName = 'FormInput';

export { FormInput };

